import { enqueueTask } from 'ember-concurrency';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import { saveRecord } from 'tio-data/requests/save-record';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import AttachmentList from './upload-attachments/attachment-list.gts';
import TuitionAssistanceFormsUploadAttachmentsUploader from './upload-attachments/uploader.gts';
import type DigitalAssetsService from '../../../services/digital-assets.ts';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/** The three polymorphic models that can own TasAssets via a hasMany `tasAssets`. */
type Attachable = TasProgramInstanceModel | TasCourseModel | TasApplicationModel;

/** The attachable model names a caller passes as `@attachableType`. */
type AttachableType = 'tasApplication' | 'tasCourse' | 'tasProgramInstance';

/** TasAsset's inverse hasMany relationship keys. */
type AttachableRelationshipKey = 'tasProgramInstances' | 'tasCourses' | 'tasApplications';

/** Maps each attachable model name to TasAsset's matching hasMany relationship. */
const ATTACHABLE_RELATIONSHIP_KEY: Record<AttachableType, AttachableRelationshipKey> = {
  tasApplication: 'tasApplications',
  tasCourse: 'tasCourses',
  tasProgramInstance: 'tasProgramInstances',
};

export interface UploadAttachmentsSignature {
  Args: {
    attachable: Attachable;
    attachableType: AttachableType;
    programTemplate: TasProgramTemplateModel;
    classification: string;
    uploadRequired?: boolean;
    disabled?: boolean;
    useDropzone?: boolean;
    iconOnlyUpload?: boolean;
    hideInstructions?: boolean;
    hideAttachments?: boolean;
    instructions?: string;
    items?: string[];
    attachmentListItems?: TasAssetModel[];
    hideDiscarded?: boolean;
    didUploadFile?: () => void;
  };
  Element: HTMLDivElement;
}

export default class UploadAttachmentsComponent extends Component<UploadAttachmentsSignature> {
  /*
  COURSE_APPROVAL_DOCUMENTS - course approval
  COMPLETION_DOCUMENTS - course evidence
  PROGRAM_APPROVAL_DOCUMENTS - program approval
  */
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;

  get instructionsField() {
    return `${this.args.classification}_STRING`;
  }

  get instructions() {
    return this.args.instructions
      ? this.args.instructions
      : // @ts-expect-error: not sure about this one
        getSingleValueForTasField(this.instructionsField, this.args.programTemplate?.fields) || '';
  }

  get attachmentItemsField() {
    return `${this.args.classification}_ATTACHMENT_FIELD`;
  }

  get attachmentItems() {
    return this.args.items
      ? this.args.items
      : // @ts-expect-error: not sure about this one
        getAllValuesForTasField(this.attachmentItemsField, this.args.programTemplate?.fields) || [];
  }

  get assetsForAttachmentList() {
    return this.args.attachmentListItems ?? this.args.attachable?.tasAssets ?? [];
  }

  didUploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);

    asset.classification = this.args.classification;

    const attachable = this.args.attachable;
    const relationshipKey = ATTACHABLE_RELATIONSHIP_KEY[this.args.attachableType];

    if (attachable.isNew) {
      // Only TasCourse has a `tasApplication` belongsTo; narrow the union before access.
      if ('tasApplication' in attachable && attachable.tasApplication?.isNew) {
        await saveRecord(this.store, attachable.tasApplication);
      }
      // Only TasApplication has a `tasProgramInstance` belongsTo; narrow before access.
      if ('tasProgramInstance' in attachable && attachable.tasProgramInstance?.isNew) {
        await saveRecord(this.store, attachable.tasProgramInstance);
      }
      await saveRecord(this.store, attachable);
    }

    // `asset[relationshipKey]` is a union of hasMany relationships, so cast to
    // the loosest common element shape for the structurally-identical `push`.
    (asset[relationshipKey] as Attachable[]).push(attachable);
    attachable.tasAssets.push(asset);

    await saveRecord(this.store, asset);
    await saveRecord(this.store, attachable);

    if (this.args.didUploadFile) {
      this.args.didUploadFile();
    }
  });

  <template>
    <div class="tio-col-flush w-full" ...attributes>
      {{#unless @hideInstructions}}
        <p class="font-semibold">
          {{! @glint-expect-error: connected to the above ts-expect-error in instructions}}
          {{this.instructions}}
          {{#if @uploadRequired}}
            <span class="tio-fg-danger">*</span>
          {{/if}}
        </p>

        {{#if this.attachmentItems.length}}
          <ul class="font-medium tio-fg-secondary list-disc list-inside py-2">
            {{#each this.attachmentItems as |item|}}
              {{! @glint-expect-error: connected to the above ts-expect-error in attachmentItems}}
              <li class="px-4">{{item}}</li>
            {{/each}}
          </ul>
        {{/if}}
      {{/unless}}

      <TuitionAssistanceFormsUploadAttachmentsUploader
        @didUploadFile={{this.didUploadFile.perform}}
        @isProcessing={{this.didUploadFile.isRunning}}
        @disabled={{@disabled}}
        @useDropzone={{@useDropzone}}
        @iconOnlyUpload={{@iconOnlyUpload}}
      />
      {{#unless @hideAttachments}}
        <AttachmentList
          @assets={{this.assetsForAttachmentList}}
          @disabled={{@disabled}}
          @hideDiscarded={{@hideDiscarded}}
        />
      {{/unless}}
    </div>
  </template>
}
